/* PaywallCard — generic locked-feature card shown when a user lacks
 * the subscription level required to view a feature's content.
 *
 * Why it exists:
 *   Showing an empty list ("لا توجد قرارات مطابقة") to a free-tier user is
 *   actively harmful — the lawyer (and worse, his client looking over his
 *   shoulder) reads it as "the app is broken / empty" and trust collapses.
 *   This card replaces every silent empty-state behind a subscription gate
 *   with a confident, conversion-friendly upgrade prompt.
 *
 * Design intent:
 *   - Premium feel that mirrors LexIQ's gold-on-navy identity.
 *   - One dominant CTA → /account/subscription (no decision fatigue).
 *   - Real corpus size shown as social proof when the backend returns it
 *     (`total_locked` for cassation). Makes the lock feel like a vault,
 *     not an absence.
 */
import { Link } from "react-router-dom";
import {
  LockKey, Crown, ArrowLeft, CheckCircle, Sparkle,
} from "@phosphor-icons/react";
import { isIOSNative } from "../../lib/platform";

export default function PaywallCard({
  // Visual + copy
  icon: Icon = LockKey,
  title = "هذه الميزة بانتظار ترقية حسابك",
  subtitle = "خطّتك الحاليّة لا تشمل هذا القسم.",
  highlights = [],            // [{icon?, text}] — bullet list of perks
  // The hard number to flex (e.g. "487 قرار تمييزيّ") — optional.
  unlockCount,
  unlockNoun = "عنصر",        // e.g. "قرار تمييزيّ", "قانون", "نظام"
  // CTA
  ctaTo = "/account/subscription",
  ctaLabel = "ارقَ لِخطّة Pro",
  // Optional secondary CTA (kept low-emphasis; default hidden)
  secondaryLabel,
  secondaryOnClick,
  // Whether this card is rendered inline inside a screen with its own
  // header (compact = no big top icon). Default is the full hero look.
  compact = false,
  className = "",
  dataTestId = "paywall-card",
}) {
  return (
    <div
      dir="rtl"
      data-testid={dataTestId}
      className={
        "relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#0A192F] via-[#0E1F38] to-[#0A192F] " +
        "text-white shadow-[0_8px_40px_-12px_rgba(10,25,47,0.5)] " +
        "border border-[#D4AF37]/35 " +
        (compact ? "p-5 sm:p-6 " : "p-6 sm:p-8 ") +
        className
      }
    >
      {/* Decorative gold radial glow + grain — gives the navy a richness
          that flat colour cannot. Pure CSS, no asset cost. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            "radial-gradient(circle at 15% 10%, rgba(212,175,55,0.22) 0%, transparent 45%)," +
            "radial-gradient(circle at 85% 90%, rgba(212,175,55,0.12) 0%, transparent 50%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.035] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' /></filter><rect width='100%25' height='100%25' filter='url(%23n)' /></svg>\")",
        }}
      />

      <div className="relative z-10">
        {/* Top crest: small badge + lock icon stack */}
        {!compact && (
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div
                className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A88425] flex items-center justify-center
                           shadow-[0_8px_28px_-8px_rgba(212,175,55,0.7)]"
              >
                <Icon size={30} weight="duotone" className="text-[#0A192F]" />
              </div>
              <div className="absolute -top-1 -right-1 bg-[#0A192F] border-2 border-[#D4AF37] rounded-full p-1">
                <LockKey size={12} weight="fill" className="text-[#D4AF37]" />
              </div>
            </div>
          </div>
        )}

        {/* Title + subtitle */}
        <h2
          className={
            "font-extrabold text-center mb-1.5 leading-tight " +
            (compact ? "text-[18px]" : "text-[22px] sm:text-[24px]")
          }
        >
          {title}
        </h2>
        <p className="text-center text-[13px] sm:text-[14px] text-white/75 leading-relaxed max-w-md mx-auto mb-5">
          {subtitle}
        </p>

        {/* Big number teaser — only when supplied */}
        {unlockCount !== undefined && unlockCount !== null && (
          <div className="text-center mb-5">
            <div className="inline-flex items-baseline gap-1.5 bg-white/[0.06] border border-[#D4AF37]/30 rounded-full px-4 py-1.5">
              <span className="text-[#D4AF37] font-extrabold text-[18px] tabular-nums">
                {Number(unlockCount).toLocaleString("ar-EG")}
              </span>
              <span className="text-white/80 text-[12px]">{unlockNoun} بانتظارك</span>
            </div>
          </div>
        )}

        {/* Perks list */}
        {highlights.length > 0 && (
          <ul className="space-y-1.5 mb-5 max-w-sm mx-auto">
            {highlights.map((h, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-[13px] text-white/85 leading-snug"
              >
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="text-[#D4AF37] shrink-0 mt-0.5"
                />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        {/* CTA group — عَلى iOS Native نَعرِض نَصّاً بَديلاً بَدَل زَرّ
            الترقية، لِلاِمتِثال لِـ Apple Guideline 3.1.3(b) الَّذي يَمنَع
            "steering" المُستَخدِمين لِبَوّابات دَفع خارِجيّة. */}
        {isIOSNative() ? (
          <div
            className="text-center text-[13px] text-white/80 leading-[1.85] max-w-sm mx-auto bg-white/[0.06] border border-white/15 rounded-2xl px-4 py-3"
            data-testid={`${dataTestId}-ios-note`}
          >
            تُدار الاِشتِراكات وَالتَّرقيات مِن نُسخَة الوِيب. سَجِّل الدُّخول
            بِنَفس بَريدِك الإلِكتروني عَلى المُتَصَفِّح لِإدارَة باقَتِك.
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2.5">
            <Link
              to={ctaTo}
              data-testid={`${dataTestId}-cta`}
              className="group inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full
                         bg-gradient-to-l from-[#D4AF37] to-[#E6C463] text-[#0A192F]
                         font-extrabold text-[15px] shadow-[0_8px_24px_-6px_rgba(212,175,55,0.55)]
                         hover:brightness-105 active:scale-[0.97] transition w-full sm:w-auto"
            >
              <Crown size={18} weight="fill" />
              <span>{ctaLabel}</span>
              <ArrowLeft
                size={16}
                weight="bold"
                className="transition-transform group-hover:-translate-x-1"
              />
            </Link>
            {secondaryLabel && (
              <button
                type="button"
                onClick={secondaryOnClick}
                data-testid={`${dataTestId}-secondary`}
                className="text-white/70 hover:text-white text-[12.5px] underline-offset-4 hover:underline transition"
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        )}

        {/* Social proof footer — soft, easy to remove if it grates */}
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-center gap-1.5 text-white/55 text-[11px]">
          <Sparkle size={11} weight="fill" className="text-[#D4AF37]" />
          <span>محتوى مُتجَدِّد أسبوعياً ومُحَقَّق من مصادر رسمية</span>
        </div>
      </div>
    </div>
  );
}
